import * as z from 'zod';

export const AppendOrCreateContactRequest = z.object({
  options: z
    .object({
      info: z
        .object({
          name: z
            .object({
              first: z
                .string()
                .describe("Contact's first name.")
                .max(1000)
                .optional()
                .nullable(),
              last: z
                .string()
                .describe("Contact's last name.")
                .max(1000)
                .optional()
                .nullable(),
            })
            .describe("Contact's first and last name.")
            .optional(),
          emails: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Email ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'MAIN', 'HOME', 'WORK'])
                      .optional(),
                    email: z
                      .string()
                      .describe('Email address.')
                      .min(1)
                      .max(320)
                      .optional(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary email address.\nWhen changing `primary` to `true` for an email,\nthe contact's other emails become `false`.\nChanging the primary email of a contact also affects the subscription status to marketing emails that are decided based on the primary email."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's email addresses.")
            .optional(),
          phones: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Phone ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum([
                        'UNTAGGED',
                        'MAIN',
                        'HOME',
                        'MOBILE',
                        'WORK',
                        'FAX',
                      ])
                      .optional(),
                    countryCode: z
                      .string()
                      .describe(
                        '[ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) country code.'
                      )
                      .optional()
                      .nullable(),
                    phone: z
                      .string()
                      .describe('Phone number.')
                      .min(1)
                      .max(50)
                      .optional(),
                    e164Phone: z
                      .string()
                      .describe(
                        '[ITU E.164-formatted](https://www.itu.int/rec/T-REC-E.164/)\nphone number.\nAutomatically generated using `phone` and `countryCode`, pending both values are valid.'
                      )
                      .optional()
                      .nullable(),
                    primary: z
                      .boolean()
                      .describe(
                        "Indicates whether this is the contact's primary phone number.\nWhen changing `primary` to `true` for a phone,\nthe contact's `primary` field for other phones becomes `false`.\nChanging the primary phone number also affects the subscription status to SMS messages that are decided based on the primary phone."
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's phone numbers.")
            .optional(),
          addresses: z
            .object({
              items: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Street address ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    tag: z
                      .enum(['UNTAGGED', 'HOME', 'WORK', 'BILLING', 'SHIPPING'])
                      .optional(),
                    address: z
                      .object({
                        city: z.string().optional().nullable(),
                        subdivision: z.string().optional().nullable(),
                        country: z.string().optional().nullable(),
                        postalCode: z.string().optional().nullable(),
                        addressLine1: z.string().optional().nullable(),
                        addressLine2: z.string().optional().nullable(),
                      })
                      .describe('Street address.')
                      .optional(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe("Contact's street addresses.")
            .optional(),
          company: z
            .string()
            .describe("Contact's company name.")
            .max(1000)
            .optional()
            .nullable(),
          jobTitle: z
            .string()
            .describe("Contact's job title.")
            .max(1000)
            .optional()
            .nullable(),
          birthdate: z
            .string()
            .describe(
              'Birth date in `YYYY-MM-DD` format. For example, `2020-03-15`.'
            )
            .optional()
            .nullable(),
          locale: z
            .string()
            .describe(
              'Locale in\n[IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.\nTypically, this is a lowercase 2-letter language code,\nfollowed by a hyphen, followed by an uppercase 2-letter country code.\nFor example, `en-US` for U.S. English, and `de-DE` for Germany German.'
            )
            .optional()
            .nullable(),
          labelKeys: z
            .object({ items: z.array(z.string()).max(2000).optional() })
            .describe(
              "List of contact's labels.\n\nLabels are used to organize contacts. Labels can be\nadded and removed using Label Contact and Unlabel Contact, respectively.\n\nTo view or manage contact labels, use the Labels API."
            )
            .optional(),
          extendedFields: z
            .object({
              items: z
                .record(z.string(), z.any())
                .describe(
                  "Contact's extended fields,\nwhere each key is the field key,\nand each value is the field's value for the contact.\n\nTo view and manage extended fields, use the Extended Fields API."
                )
                .optional(),
            })
            .describe(
              'Additional custom fields.\n\nEmpty fields are not returned.'
            )
            .optional(),
          picture: z
            .object({
              image: z
                .string()
                .describe(
                  'Image.\nThis can contain an image URL or a Wix Media image ID.'
                )
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
        })
        .describe(
          "Contact's information.\nTo reconcile with an existing contact,\na phone or email must be provided,\nor the current session identity must be attached to a contact or member.\n\nIf no existing contact can be found,\na new contact is created with the specified information.\n\n<!--ONLY:REST-->\nRequired if there's no contextual identity or given `contactId`.\n<!--END:ONLY:REST-->"
        )
        .optional(),
      passThroughData: z
        .string()
        .describe(
          'Pass through data, to be posted on Contact Submitted Domain Event (optional).'
        )
        .max(300)
        .optional()
        .nullable(),
      contactId: z
        .string()
        .describe(
          'Existing Contact ID (optional).\nRequires permission `CONTACTS.SUBMIT_WITH_OVERRIDES`'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const AppendOrCreateContactResponse = z.object({
  contactId: z
    .string()
    .describe('ID of the contact that was found or created.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  identityType: z
    .enum(['UNKNOWN', 'CONTACT', 'MEMBER', 'NOT_AUTHENTICATED_MEMBER'])
    .describe(
      'Identity type of the returned contact.\n\n- `CONTACT`: The returned contact ID belongs to a new or existing contact.\n- `MEMBER`: The returned contact ID belongs to the currently logged-in site member.\n- `NOT_AUTHENTICATED_MEMBER`: The returned contact ID belongs to a site member who is not currently logged in.'
    )
    .optional(),
  newContact: z
    .boolean()
    .describe(
      'Indicates whether the contact was just created or already existed.\n\nIf the contact was just created, returns `true`.\nIf it already existed, returns `false`.'
    )
    .optional(),
});
